import { useEffect, useRef } from 'react';
import { removeCssFromMarkdown } from '@dealstack/frontend/app/pages/Project/MIP/Messages/components/Email/Email.utils';
import { FlexExtended } from '@dealstack/frontend/shared/components/Flex/FlexExtended';
import { useTranslationMipMessages } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Flex, Skeleton, Text } from '@mantine/core';
import Markdown from 'react-markdown';
import { StyledIframe, iframeStyles } from './styled';
import { hasDoctype } from '@dealstack/frontend/app/pages/Project/MIP/Messages/components/Email/utils';

interface Props {
	subject: string;
	sentToEmail: string;
	emailBody: string;
	isLoading: boolean;
}

export const Email = ({ subject, sentToEmail, emailBody, isLoading }: Props) => {
	const tMipMessages = useTranslationMipMessages();
	const formattedEmailBody = removeCssFromMarkdown(emailBody);
	const emailHasDocType = hasDoctype(emailBody);

	const iframeRef = useRef<HTMLIFrameElement>(null);
	useEffect(() => {
		if (iframeRef.current) {
			const iframe = iframeRef.current;
			const doc = iframe.contentDocument || iframe.contentWindow?.document;

			if (doc) {
				doc.documentElement.innerHTML = emailBody;

				// Add css to make content non-clickable
				const style = doc.createElement('style');
				style.textContent = iframeStyles;
				doc.head.appendChild(style);
			}
		}
	}, [emailBody]);

	return (
		<FlexExtended direction="column" gap="1rem" verticalScroll={true} w="100%">
			<Flex direction="row" gap="1rem" justify="space-between">
				<Flex direction="column">
					<Text c="gray.9" fw={600} mb="0.25rem">
						{tMipMessages('subject')}
					</Text>
					{isLoading ? (
						<Skeleton>
							<Text fw={600}>{subject}</Text>
						</Skeleton>
					) : (
						<Text fw={600}>{subject}</Text>
					)}
				</Flex>
				<Flex direction="column">
					<Text c="gray.9" fw={600} mb="0.25rem">
						{tMipMessages('sentTo')}
					</Text>
					{isLoading ? (
						<Skeleton>
							<Text fw={600}>{sentToEmail}</Text>
						</Skeleton>
					) : (
						<Text fw={600}>{sentToEmail}</Text>
					)}
				</Flex>
			</Flex>
			<Flex direction="column" h="90%" mah="90%" w="100%">
				<Text c="gray.9" fw={600} mb="0.25rem">
					{tMipMessages('emailTitle')}
				</Text>

				{!emailHasDocType && (
					<Text>
						{isLoading ? (
							<>
								<Skeleton height={20} radius="sm" mb="0.25rem" />
								<Skeleton height={20} radius="sm" mb="0.25rem" />
								<Skeleton height={20} radius="sm" mb="0.25rem" />
							</>
						) : (
							<Markdown
								// Remove links and hide images and
								components={{
									a: ({ children }) => (
										<a
											target="_blank"
											rel="noopener noreferrer"
											style={{
												color: '#007bff',
												textDecoration: 'underline',
											}}
										>
											{children}
										</a>
									),
									img: () => <></>,
								}}
							>
								{formattedEmailBody}
							</Markdown>
						)}
					</Text>
				)}
				{emailHasDocType && (
					<Flex w="100%" h="100%">
						<StyledIframe ref={iframeRef} title="Email content" />
					</Flex>
				)}
			</Flex>
		</FlexExtended>
	);
};
